import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import NavBadge from '../NavBadge';
import { useOpenItemsCount } from '../../hooks/useOpenItemsCount';

interface CoreLayoutProps {
  children: React.ReactNode;
}

const menuItems = [
  { icon: 'ri-dashboard-line', label: 'Dashboard', path: '/dashboard' },
  { icon: 'ri-inbox-line', label: 'Open Items', path: '/open-items' },
  { icon: 'ri-user-line', label: 'Callers', path: '/user-memories' },
  { icon: 'ri-team-line', label: 'Team', path: '/team' },
  { icon: 'ri-arrow-left-right-line', label: 'Transfer', path: '/transfer' },
  { icon: 'ri-file-list-line', label: 'Call Logs', path: '/call-logs' },
  { icon: 'ri-phone-line', label: 'Phone System', path: '/phone-system-app' },
  { icon: 'ri-book-line', label: 'Knowledge Base', path: '/knowledge-base' },
  { icon: 'ri-links-line', label: 'Integrations', path: '/integrations' },
  { icon: 'ri-bank-card-line', label: 'Billing', path: '/billing' },
];

export default function CoreLayout({ children }: CoreLayoutProps) {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const openItemsCount = useOpenItemsCount();

  const handleNavigate = (path: string) => {
    setSidebarOpen(false);
    window.REACT_APP_NAVIGATE(path);
  };

  const handleLogout = async () => {
    try {
      // Clears the HttpOnly session cookie (server-side)
      await fetch('https://app.neurospherevoiceai.com/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
    } catch (err) {
      // Even if this fails, still clear client hints and redirect.
      console.warn('Logout request failed:', err);
    }

    // Clear local UI hints (your app uses these)
    localStorage.removeItem('customerId');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('ns_onboarding_v1');

    // Go to login (as requested)
    window.location.href = 'https://neurospherevoiceai.com/login';
  };

  return (
    <div className="flex min-h-screen bg-transparent">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-screen w-[190px] bg-[#0d0d12]/85 backdrop-blur-md border-r flex flex-col z-50 transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
        style={{ borderColor: 'rgba(138, 43, 226, 0.25)' }}
      >
        {/* Logo */}
        <div
          className="px-4 py-5 border-b overflow-visible"
          style={{ borderColor: 'rgba(138, 43, 226, 0.15)' }}
        >
          <h1
            className="text-sm font-bold tracking-wide leading-tight cursor-pointer overflow-visible"
            style={{
              fontFamily: "'Orbitron', sans-serif",
              background: 'linear-gradient(135deg, #8a2be2 0%, #ff6a00 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              filter: 'drop-shadow(0 0 8px rgba(138, 43, 226, 0.4))',
              whiteSpace: 'nowrap',
            }}
            onClick={() => handleNavigate('/dashboard')}
          >
            NEUROSPHERE
          </h1>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 py-6 px-2 overflow-y-auto">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;

            return (
              <button
                key={item.path}
                onClick={() => handleNavigate(item.path)}
                className={`relative w-full flex items-center gap-2.5 px-3 py-2.5 mb-1.5 rounded-md transition-colors duration-200 cursor-pointer ${
                  isActive
                    ? 'bg-[#8a2be2]/20 text-white'
                    : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
                }`}
              >
                {isActive && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 rounded-r bg-[#8a2be2]" />
                )}

                <div className="w-5 h-5 shrink-0 flex items-center justify-center">
                  <i className={`${item.icon} text-lg`} aria-hidden />
                </div>

                <span
                  className="flex-1 min-w-0 text-left text-sm font-medium truncate"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  {item.label}
                </span>

                {item.path === '/open-items' && <NavBadge count={openItemsCount} />}
              </button>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t" style={{ borderColor: 'rgba(138, 43, 226, 0.15)' }}>
          <div className="flex items-center gap-2 text-xs text-purple-400/60">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span style={{ fontFamily: "'Space Grotesk', sans-serif" }}>System Online</span>
          </div>

          {/* Logout */}
          <button
            onClick={handleLogout}
            className="mt-3 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-300 cursor-pointer"
            style={{
              background: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(138, 43, 226, 0.15)',
              color: '#aaaaaa'
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.color = '#ff6a00';
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255, 106, 0, 0.35)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.color = '#aaaaaa';
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(138, 43, 226, 0.15)';
            }}
          >
            <i className="ri-logout-box-r-line text-lg" aria-hidden />
            <span style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="relative z-10 flex-1 ml-0 md:ml-[190px] w-full">
        {/* Mobile header bar */}
        <div className="flex items-center h-14 px-4 border-b md:hidden" style={{ background: '#0d0d12', borderColor: 'rgba(138, 43, 226, 0.25)' }}>
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-gray-400 hover:text-white transition-colors"
            aria-label="Open menu"
          >
            <i className="ri-menu-line text-2xl" aria-hidden />
          </button>
          <span
            className="ml-4 text-sm font-bold tracking-wider"
            style={{
              fontFamily: "'Orbitron', sans-serif",
              background: 'linear-gradient(135deg, #8a2be2 0%, #ff6a00 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            NEUROSPHERE
          </span>
        </div>
        <div className="min-h-screen">{children}</div>
      </main>

      {/* Background Grid Overlay */}
      <div className="fixed inset-0 pointer-events-none opacity-5 z-0">
        <div
          className="w-full h-full"
          style={{
            backgroundImage: `
              linear-gradient(90deg, rgba(138,43,226,0.3) 1px, transparent 1px),
              linear-gradient(rgba(138,43,226,0.3) 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px'
          }}
        />
      </div>
    </div>
  );
}
